import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { usePagination, SearchBar } from '../components/Pagination';

export default function ValidationNotes() {
  const [rows, setRows]     = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({ classId: '', subjectId: '', periodId: '', validation: '' });
  const [periods, setPeriods] = useState([]);
  const [error, setError]   = useState('');
  const [flash, setFlash]   = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/catalog/classes'),
      api.get('/catalog/subjects'),
      api.get('/catalog/periods'),
    ]).then(([cls, sub, per]) => {
      setClasses(cls || []);
      setSubjects(sub || []);
      setPeriods(per || []);
    }).catch(() => {});
    load();
  }, []);

  function load() {
    const qs = [];
    if (filters.classId)   qs.push(`classId=${filters.classId}`);
    if (filters.subjectId) qs.push(`subjectId=${filters.subjectId}`);
    if (filters.periodId)  qs.push(`periodId=${filters.periodId}`);
    api.get(`/evaluations${qs.length ? `?${qs.join('&')}` : ''}`)
      .then(setRows).catch((e) => setError(e.message));
  }

  async function validate(evalId) {
    try {
      await api.post(`/evaluations/${evalId}/validate`, {});
      setFlash('Évaluation validée et publiée ✓');
      setTimeout(() => setFlash(''), 3000);
      load();
    } catch (e) { setError(e.message); }
  }

  const filtered = rows.filter((r) => {
    if (filters.validation === 'valide' && r.status !== 'VALIDE' && r.status !== 'PUBLIE') return false;
    if (filters.validation === 'en_attente' && (r.status === 'VALIDE' || r.status === 'PUBLIE')) return false;
    return true;
  });

  const STATUS_MAP = {
    BROUILLON:   { cls: 'gray',  l: 'Brouillon'  },
    VALIDE:      { cls: 'blue',  l: 'Validée'     },
    PUBLIE:      { cls: 'green', l: 'Publiée'     },
    CORRIGE:     { cls: 'navy',  l: 'Corrigée'    },
    CLOTURE:     { cls: 'gold',  l: 'Clôturée'    },
  };


  const { paginated: notesPaginated, search: vSearch, setSearch: setVSearch, Pagination: VPagination, total: vTotal, filtered: vFiltered } = usePagination(filtered, { pageSize: 5, searchKeys: ['title', 'status'] });
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div>
          <h1 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: 4 }}>Validation des Notes</h1>
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>Supervisez l'avancement de la saisie des notes et validez-les pour publication.</p>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {flash && <div className="receipt-banner">{flash}</div>}

      {/* Filtres */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: 10, marginBottom: 16, alignItems: 'flex-end' }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Classe</label>
          <select value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
            <option value="">Toutes les classes</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Matière</label>
          <select value={filters.subjectId} onChange={(e) => setFilters({ ...filters, subjectId: e.target.value })}>
            <option value="">Toutes les matières</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Semestre / Trimestre</label>
          <select value={filters.periodId} onChange={(e) => setFilters({ ...filters, periodId: e.target.value })}>
            <option value="">Tous</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Validation</label>
          <select value={filters.validation} onChange={(e) => setFilters({ ...filters, validation: e.target.value })}>
            <option value="">Tous</option>
            <option value="valide">Validées</option>
            <option value="en_attente">En attente</option>
          </select>
        </div>
        <button className="btn btn-sm" onClick={load} style={{ height: 38 }}>Filtrer</button>
      </div>

      {/* Liste des évaluations groupées par classe+matière */}
      <SearchBar value={vSearch} onChange={setVSearch} placeholder="Titre, classe, matière…" total={vTotal} filtered={vFiltered.length} />
      {vFiltered.length === 0 ? (
        <div className="card"><div className="empty">Aucune évaluation trouvée.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {notesPaginated.map((ev) => {
            const results = ev.results || [];
            const total   = results.length;
            const renseignes = results.filter((r) => !r.absent && r.totalObtained != null).length;
            const pct = total > 0 ? Math.round(renseignes / total * 100) : 0;
            const { cls, l } = STATUS_MAP[ev.status] || { cls: 'gray', l: ev.status };
            const canValidate = ev.status === 'CORRIGE' || (ev.status === 'BROUILLON' && renseignes > 0);

            return (
              <div key={ev.id} className="card">
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                        <b style={{ fontSize: 14 }}>{ev.class?.name || '—'}</b>
                        <span style={{ color: 'var(--muted)', fontSize: 12 }}>{ev.academicYear?.name || ''}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                        {ev.subject?.name || '—'}
                        {ev.subject?.code && <span style={{ marginLeft: 8, fontSize: 11 }}>Code : {ev.subject.code}</span>}
                        {ev.coefficient && <span style={{ marginLeft: 8, fontSize: 11 }}>Coeff. {ev.coefficient}</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                        {ev.teacher ? `${ev.teacher.lastName} ${ev.teacher.firstName}` : '—'}
                        {ev.period?.name && <span style={{ marginLeft: 8 }}>{ev.period.name}</span>}
                      </div>
                    </div>
                    <span className={`badge ${cls}`}>{l}</span>
                  </div>

                  {/* Barre d'avancement */}
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
                        <span>{ev.title}</span>
                        <span>{renseignes}/{total} notes · {pct}%</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--line)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? 'var(--success)' : 'var(--primary)', borderRadius: 3, transition: 'width .3s' }} />
                      </div>
                    </div>

                    {canValidate ? (
                      <button className="btn btn-sm"
                        style={{ background: '#d97706', border: 'none', flexShrink: 0 }}
                        onClick={() => validate(ev.id)}>
                        VALIDER
                      </button>
                    ) : ev.status === 'VALIDE' || ev.status === 'PUBLIE' ? (
                      <span className="badge green" style={{ flexShrink: 0 }}>✓ Validé</span>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        <VPagination />
        </div>
      )}
    </div>
  );
}
